import { ListItemText, Stack, ListItemButton, Divider, styled, Skeleton } from '@mui/material';
import { getAccount } from '@qdrant/qdrant-cloud-public-api/account/v1/connect-query';
import { listManagementKeys } from '@qdrant/qdrant-cloud-public-api/auth/v1/auth/connect-query';
import { listAccountOAuthGrants } from '@qdrant/qdrant-cloud-public-api/auth/v1/oauth/connect-query';
import { listBackups } from '@qdrant/qdrant-cloud-public-api/cluster/backup/v1/connect-query';
import { listClusters } from '@qdrant/qdrant-cloud-public-api/cluster/v1/connect-query';
import { listHybridCloudEnvironments } from '@qdrant/qdrant-cloud-public-api/hybrid/v1/connect-query';
import { listRoles, listUsers } from '@qdrant/qdrant-cloud-public-api/iam/v1/connect-query';
import { listPaymentMethods } from '@qdrant/qdrant-cloud-public-api/payment/v1/connect-query';
import { listSpaces } from '@qdrant/qdrant-cloud-public-api/serverless/space/v1/connect-query';
import {
  BoxesIcon,
  CircleHelpIcon,
  DatabaseBackupIcon,
  ServerIcon,
  SettingsIcon,
  CreditCardIcon,
  UsersIcon,
  RocketIcon,
} from 'lucide-react';
import { type RefObject, Suspense, memo, useCallback } from 'react';
import { WithRpcPermissions } from '@/components/AccessManagement/WithRpcPermissions/WithRpcPermissions';
import { PremiumPill } from '@/components/Clusters/PremiumPill';
import { type ScrollbarRef } from '@/components/Common/Scrollbar';
import { SupportLink } from '@/components/Common/SupportLink/SupportLink';
import { HybridCloudIcon } from '@/components/Icon/generated';
import { useFeatureGateOAuth, useFeatureGateServerless } from '@/helpers/statsig/featureGate';
import { useEffectivePermissionsForAccountId } from '@/hooks/useEffectivePermissionsForAccountId';
import {
  type AccountId,
  accountIdUrlParam,
  useSelectedAccount,
} from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { DashboardAccountNavigation } from './DashboardAccountNavigation';
import { Nav, NavItem, NavItemIcon } from './DashboardNavCommon';
import { DashboardSidebarNavigationSkeleton } from './DashboardSidebarNavigationSkeleton';
import { DashboardUserNavigation } from './DashboardUserNavigation';

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateRows: 'auto auto 1fr',
  gap: theme.spacing(1),
  padding: theme.spacing(3, 2, 2),
  height: '100%',
}));

export const DashboardSidebarNavigation = memo(function DashboardSidebarNavigation({
  scrollbarRef,
}: {
  scrollbarRef: RefObject<ScrollbarRef | null>;
}) {
  const handleUserNavigationOpen = useCallback(() => {
    scrollbarRef.current?.recalculate();
  }, [scrollbarRef]);

  return (
    <StyledContainer>
      <Suspense fallback={<Skeleton variant="rounded" height={40} />}>
        <DashboardAccountNavigation />
      </Suspense>
      <Nav aria-label="Account navigation">
        <Suspense fallback={<DashboardSidebarNavigationSkeleton />}>
          <Stack sx={{ gap: 1 }}>
            <GetStartedNavItem />
            <DashboardAccountNavigationItems />
          </Stack>
        </Suspense>
      </Nav>

      <Stack sx={{ mt: 'auto', gap: 1 }} divider={<Divider />}>
        <Suspense fallback={<Skeleton variant="rounded" height={40} />}>
          <DashboardUserNavigation onOpen={handleUserNavigationOpen} />
        </Suspense>
        <Suspense fallback={<Skeleton variant="rounded" height={40} />}>
          <SupportLinkNavigationItem />
        </Suspense>
      </Stack>
    </StyledContainer>
  );
});

const GetStartedNavItem = () => {
  const account = useSelectedAccount();
  return (
    <NavItem to="/get-started" icon={RocketIcon} search={{ account_id: account?.id }}>
      Get Started
    </NavItem>
  );
};

const SupportLinkNavigationItem = () => {
  const account = useSelectedAccount();
  const isPremiumAccount = Boolean(account?.privileges.includes('PREMIUM_TIER'));

  return (
    <SupportLink component={ListItemButton} dense={true} divider={false}>
      <NavItemIcon icon={CircleHelpIcon} />
      <ListItemText primary="Get Support" />
      {isPremiumAccount && <PremiumPill />}
    </SupportLink>
  );
};

const DashboardAccountNavigationItems = () => {
  const account = useSelectedAccount();

  if (!account) {
    return null;
  }

  return <SelectedAccountNavigationItems accountId={accountIdUrlParam.parse(account.id)} />;
};

const SelectedAccountNavigationItems = ({ accountId }: { accountId: AccountId }) => {
  const isServerlessEnabled = useFeatureGateServerless();
  const isOAuthEnabled = useFeatureGateOAuth();
  const isBillingPageHidden = Boolean(useSelectedAccount()?.privileges.includes('HIDE_BILLING_PAGE'));
  useEffectivePermissionsForAccountId(accountId);

  const params = { accountId };

  return (
    <>
      <WithRpcPermissions
        methods={[listClusters]}
        render={() => (
          <NavItem to="/accounts/$accountId/clusters" icon={ServerIcon} params={params}>
            Clusters
          </NavItem>
        )}
      />
      {isServerlessEnabled && (
        <WithRpcPermissions
          methods={[listSpaces]}
          render={() => (
            <NavItem to="/accounts/$accountId/serverless-spaces" icon={BoxesIcon} params={params}>
              Serverless Spaces
            </NavItem>
          )}
        />
      )}
      <WithRpcPermissions
        methods={[listHybridCloudEnvironments]}
        render={() => (
          <NavItem to="/accounts/$accountId/hybrid-cloud" icon={HybridCloudIcon} params={params}>
            Hybrid Cloud
          </NavItem>
        )}
      />
      <WithRpcPermissions
        methods={[listBackups, listClusters]}
        render={() => (
          <NavItem to="/accounts/$accountId/backups" icon={DatabaseBackupIcon} params={params}>
            Backups
          </NavItem>
        )}
      />
      <WithRpcPermissions
        methods={[listManagementKeys, listRoles, listUsers, ...(isOAuthEnabled ? [listAccountOAuthGrants] : [])]}
        combinator="anyOf"
        render={() => (
          <NavItem to="/accounts/$accountId/cloud-access" icon={UsersIcon} params={params}>
            Access Management
          </NavItem>
        )}
      />
      {!isBillingPageHidden && (
        <WithRpcPermissions
          methods={[listPaymentMethods]}
          render={() => (
            <NavItem to="/accounts/$accountId/billing" icon={CreditCardIcon} params={params}>
              Billing
            </NavItem>
          )}
        />
      )}
      <WithRpcPermissions
        methods={[getAccount]}
        render={() => (
          <NavItem to="/accounts/$accountId/settings" icon={SettingsIcon} params={params}>
            Settings
          </NavItem>
        )}
      />
    </>
  );
};
